import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import TubeImage from "../../assets/images/Tube.png";
import tubeHotspots, { tubeSectionContent } from "./tubeHotspots";

export default function TubeInteractiveSection() {
  const [activeTubeHotspotId, setActiveTubeHotspotId] = useState(null);
  const [detailAnimationTick, setDetailAnimationTick] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const activeTubeHotspot =
    tubeHotspots.find((hotspot) => hotspot.id === activeTubeHotspotId) || null;
  const overlayThemeVars = {
    "--home-primary-rgb": "0, 93, 133",
    "--home-muted-rgb": "156, 197, 202",
    "--home-surface-rgb": "242, 242, 242",
    "--home-accent-rgb": "147, 213, 0",
    "--home-ink-rgb": "58, 58, 58",
  };

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsExpanded(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isExpanded]);

  const handleHotspotSelect = (hotspotId) => {
    setActiveTubeHotspotId(hotspotId);
    setDetailAnimationTick((previous) => previous + 1);
  };

  const handleCanvasClick = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const clickedInteractiveElement = target.closest(
      ".home-tube-section__hotspot, .home-tube-section__detail, .home-tube-section__expand-btn, .home-tube-section__overlay-close",
    );

    if (!clickedInteractiveElement) {
      setActiveTubeHotspotId(null);
    }
  };

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isExpanded]);

  return (
    <section className="home-section home-tube-section">
      <div className="home-tube-section__content">
        <p className="home-kicker">{tubeSectionContent.kicker}</p>
        <h2>{tubeSectionContent.heading}</h2>
        <p>{tubeSectionContent.description}</p>
        <p className="home-tube-section__hint">{tubeSectionContent.hint}</p>
      </div>

      <div className="home-tube-section__media" onClick={handleCanvasClick}>
        <button
          type="button"
          className="home-tube-section__expand-btn"
          onClick={() => setIsExpanded(true)}
          aria-label="Expand tube image"
        >
          Expand
        </button>

        <img src={TubeImage} alt="Tube data flow visual" />

        <div className="home-tube-section__hotspots">
          {tubeHotspots.map((hotspot) => {
            const isActive = activeTubeHotspotId === hotspot.id;
            const isSmallHotspot = hotspot.size === "small";

            return (
              <button
                key={hotspot.id}
                type="button"
                className={[
                  "home-tube-section__hotspot",
                  isActive ? "home-tube-section__hotspot--active" : "",
                  isSmallHotspot ? "home-tube-section__hotspot--small" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  "--hotspot-x": `${hotspot.x}%`,
                  "--hotspot-y": `${hotspot.y}%`,
                }}
                onClick={() => handleHotspotSelect(hotspot.id)}
                aria-pressed={isActive}
                aria-label={hotspot.label}
              >
                <span aria-hidden="true" />
              </button>
            );
          })}
        </div>

        {activeTubeHotspot && (
          <article
            key={`inline-${activeTubeHotspotId}-${detailAnimationTick}`}
            className="home-tube-section__detail home-tube-section__detail--slide-in"
            aria-live="polite"
          >
            <p className="home-tube-section__detail-label">
              {activeTubeHotspot.label}
            </p>
            <h3>{activeTubeHotspot.title}</h3>
            <p>{activeTubeHotspot.detail}</p>
          </article>
        )}
      </div>

      {isExpanded &&
        createPortal(
          <div
            className="home-tube-section__overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Expanded tube image"
            style={overlayThemeVars}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setIsExpanded(false);
              }
            }}
          >
            <div
              className="home-tube-section__overlay-content"
              onClick={(event) => {
                event.stopPropagation();
                handleCanvasClick(event);
              }}
            >
              <button
                type="button"
                className="home-tube-section__overlay-close"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsExpanded(false);
                }}
                aria-label="Close expanded image"
              >
                Close
              </button>

              <img src={TubeImage} alt="Tube data flow visual" />

              <div className="home-tube-section__hotspots home-tube-section__hotspots--overlay">
                {tubeHotspots.map((hotspot) => {
                  const isActive = activeTubeHotspotId === hotspot.id;
                  const isSmallHotspot = hotspot.size === "small";

                  return (
                    <button
                      key={`overlay-${hotspot.id}`}
                      type="button"
                      className={[
                        "home-tube-section__hotspot",
                        isActive ? "home-tube-section__hotspot--active" : "",
                        isSmallHotspot
                          ? "home-tube-section__hotspot--small"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        "--hotspot-x": `${hotspot.x}%`,
                        "--hotspot-y": `${hotspot.y}%`,
                      }}
                      onClick={() => handleHotspotSelect(hotspot.id)}
                      aria-pressed={isActive}
                      aria-label={hotspot.label}
                    >
                      <span aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              {activeTubeHotspot && (
                <article
                  key={`overlay-${activeTubeHotspotId}-${detailAnimationTick}`}
                  className="home-tube-section__detail home-tube-section__detail--overlay home-tube-section__detail--slide-in"
                  aria-live="polite"
                >
                  <p className="home-tube-section__detail-label">
                    {activeTubeHotspot.label}
                  </p>
                  <h3>{activeTubeHotspot.title}</h3>
                  <p>{activeTubeHotspot.detail}</p>
                </article>
              )}
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}
